/**
 * Check 2 — image shape vs the resolved placement's accept window.
 *
 * THE HONEST-UNKNOWN CHECK. Image dimensions are NOT knowable server-side in
 * this codebase: `social_posts` has no width/height/aspect column (only
 * `video_aspect_ratio`, video-only), and joining the `media` table is useless
 * — measured against prod this session, 1,188 `media_urls` entries match only
 * 18 `media` rows and exactly ONE of those carries a width (1.5% join, 0.08%
 * dimension coverage). The only source of truth for image aspect anywhere in
 * this repo is PostBody.tsx's browser `new Image()` probe into `sourceRatio`.
 *
 * Therefore a null `sourceRatio` returns `skipped`, NEVER `pass`. The edge
 * writes an honest, incomplete verdict that says it is incomplete; the client
 * upgrades it on open. That is the model, not a workaround.
 */
import { platformDisplayName } from '../../../../shared/posting-readiness.ts';
import type { PlacementRow } from '../../platform-matrix/placement-matrix.ts';
import type { MediaKind } from '../../platform-matrix/placement-resolution.ts';
import { MAJOR_CROP_THRESHOLD, MINOR_CROP_THRESHOLD, edgeCropFraction } from '../crop-math.ts';
import { applyRecommendedRatioFix } from '../fixes.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

export function checkAspect(input: {
  platform: string;
  row: PlacementRow | undefined;
  sourceRatio: number | null;
  aspectOverride: string | null;
  mediaKind: MediaKind;
}): QualityCheck {
  const { platform, row, sourceRatio, aspectOverride, mediaKind } = input;
  const base = {
    id: 'aspect' as const,
    label: mediaKind === 'video' ? 'Video shape' : CHECK_LABEL.aspect,
  };
  const name = platformDisplayName(platform);

  if (mediaKind === 'text' || !row) {
    return { ...base, status: 'skipped', detail: 'No media to check.' };
  }

  if (aspectOverride) {
    return {
      ...base,
      status: 'pass',
      detail: `Cropped to ${aspectOverride} before publishing.`,
    };
  }

  if (sourceRatio == null && mediaKind === 'video') {
    return {
      ...base,
      status: 'skipped',
      detail: `${name} ${row.placement} expects ${row.recommendedRatio} video. This video's shape isn't measured yet, so check it before publishing.`,
    };
  }

  if (sourceRatio == null) {
    return {
      ...base,
      status: 'skipped',
      detail: 'Image size not measured yet — it will be checked when you open this post.',
    };
  }

  const { fraction, axis } = edgeCropFraction(sourceRatio, row.acceptMin, row.acceptMax);

  if (fraction < MINOR_CROP_THRESHOLD) {
    return {
      ...base,
      status: 'pass',
      detail: `Fits ${name} ${row.placement} with no cropping.`,
    };
  }

  const percent = Math.round(fraction * 100);
  const sentence = `${name} will trim about ${percent}% off the ${axis}`;

  if (fraction >= MAJOR_CROP_THRESHOLD) {
    return {
      ...base,
      status: 'fail',
      detail: `${sentence} — your subject may be cut off.`,
      fix: applyRecommendedRatioFix(platform, row),
    };
  }

  return {
    ...base,
    status: 'warn',
    detail: `${sentence}.`,
    fix: applyRecommendedRatioFix(platform, row),
  };
}
